import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, EditLocationAlt, Place, Public, Search } from '@mui/icons-material';
import { colors } from '../theme/theme';

type Role = 'brand_owner' | 'studio_owner' | 'manager' | 'instructor';

export interface AccessListPerson {
  key: string;
  kind: 'staff' | 'studio';
  name: string;
  email: string | null;
  isActive: boolean;
  role: Role | null;
  activeCount: number;
  grants: { _id: string; status: 'active' | 'revoked'; scopeKind: 'brand' | 'studio'; studio: any }[];
}

interface Props {
  people: AccessListPerson[];
  showRevoked: boolean;
  onEditLocations: (p: AccessListPerson) => void;
  onRemove: (p: AccessListPerson) => void;
}

/**
 * Highest role first — the order the list is read in. `what` lives in a
 * tooltip on the role, so the list itself stays quiet.
 */
const ROLES: { role: Role | null; title: string; what: string }[] = [
  { role: 'brand_owner', title: 'Brand owners', what: 'Full control of every location, including ones added later' },
  { role: 'studio_owner', title: 'Studio owners', what: 'Full control of their locations' },
  { role: 'manager', title: 'Managers', what: 'Day-to-day operations at their locations' },
  { role: 'instructor', title: 'Instructors', what: 'Teaches at their locations' },
  { role: null, title: 'Revoked', what: 'No access any more' },
];
const metaFor = (role: Role | null) => ROLES.find((r) => r.role === role) ?? ROLES[ROLES.length - 1];

const initials = (name: string) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/** Location tiles shown before the "+N" tile. */
const MAX_TILES = 3;

const tileSx = {
  position: 'relative' as const,
  width: 28, height: 28, borderRadius: 1.5,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3,
  bgcolor: '#fdeef4', color: colors.primaryDark,
  border: `2px solid ${colors.white}`,
  boxShadow: '0 1px 3px rgba(233, 109, 154, 0.25)',
};

const GRID = { md: 'minmax(0, 2fr) 140px minmax(0, 2fr) 80px' };

/**
 * One colour per role, used for both the badge and the person's avatar so a
 * row reads at a glance. Soft tints, so four of them sit together quietly.
 */
const ROLE_STYLE: Record<Role, { bg: string; fg: string; border: string }> = {
  brand_owner: { bg: colors.primaryDark, fg: '#ffffff', border: colors.primaryDark },
  studio_owner: { bg: '#f3e5f5', fg: '#7b1fa2', border: 'transparent' },
  manager: { bg: '#e3effc', fg: '#1d5fa8', border: 'transparent' },
  instructor: { bg: '#e6f4e7', fg: '#2e7d32', border: 'transparent' },
};
const NO_ROLE_STYLE = { bg: colors.backgroundTertiary, fg: colors.textSecondary, border: colors.divider };
const styleFor = (role: Role | null) => (role ? ROLE_STYLE[role] : NO_ROLE_STYLE);

const RoleBadge: React.FC<{ role: Role | null }> = ({ role }) => {
  const m = metaFor(role);
  const s = styleFor(role);
  return (
    <Tooltip title={m.what}>
      <Chip
        size="small"
        label={role ? m.title.replace(/s$/, '') : 'No access'}
        sx={{ fontWeight: 600, borderRadius: 1.5, bgcolor: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
      />
    </Tooltip>
  );
};

/** Plain text, no chips: "Kailash Nagar, Saket +3". The full list is on hover. */
const Locations: React.FC<{ person: AccessListPerson }> = ({ person }) => {
  const active = person.grants.filter((g) => g.status === 'active');
  if (active.some((g) => g.scopeKind === 'brand')) {
    return (
      <Tooltip title="Including locations added later">
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: 'fit-content', cursor: 'default' }}>
          <Box sx={{ ...tileSx, bgcolor: colors.primaryDark, color: '#fff' }}>
            <Public sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary }}>All locations</Typography>
        </Stack>
      </Tooltip>
    );
  }
  if (!active.length) return <Typography variant="body2" color="text.disabled">—</Typography>;

  const names = active.map((g) => g.studio?.branchName || g.studio?.studioName || 'Unknown location');
  const hidden = names.length - MAX_TILES;

  /**
   * Overlapping tiles with each location's initials (HK, KN), like a stack of
   * profile pictures. A count sits beside them, and hovering shows every
   * location by name. Five locations take the same space as two.
   */
  return (
    <Tooltip
      arrow
      title={
        <Stack spacing={0.5} sx={{ py: 0.5 }}>
          {names.map((n, i) => (
            <Stack key={`${n}-${i}`} direction="row" spacing={0.75} alignItems="center">
              <Place sx={{ fontSize: 14 }} />
              <span>{n}</span>
            </Stack>
          ))}
        </Stack>
      }
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ maxWidth: '100%', width: 'fit-content', minWidth: 0, cursor: 'default' }}>
        <Stack direction="row" sx={{ flexShrink: 0 }}>
          {names.slice(0, MAX_TILES).map((n, i) => (
            <Box key={`${n}-${i}`} sx={{ ...tileSx, ml: i ? -0.75 : 0, zIndex: MAX_TILES - i }}>
              {initials(n)}
            </Box>
          ))}
          {hidden > 0 && (
            <Box sx={{ ...tileSx, ml: -0.75, bgcolor: colors.primaryDark, color: '#fff', borderColor: colors.white }}>
              +{hidden}
            </Box>
          )}
        </Stack>
        {/* Names, not a count: both when there are two, otherwise the first and "& N more". */}
        <Typography variant="body2" fontWeight={600} noWrap sx={{ color: colors.textPrimary, minWidth: 0 }}>
          {names.length <= 2 ? (
            names.join(' · ')
          ) : (
            <>
              {names[0]}
              <Box component="span" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
                {' '}& {names.length - 1} more
              </Box>
            </>
          )}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const BrandAccessList: React.FC<Props> = ({ people, showRevoked, onEditLocations, onRemove }) => {
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | 'all'>('all');

  const visible = useMemo(
    () => people.filter((p) => showRevoked || p.activeCount > 0),
    [people, showRevoked],
  );

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (p: AccessListPerson) =>
      (roleFilter === 'all' || p.role === roleFilter) &&
      (!q || p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q));
    return ROLES
      .map((r) => ({
        ...r,
        people: visible
          .filter((p) => (p.activeCount > 0 ? p.role : null) === r.role && match(p))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      }))
      .filter((g) => g.people.length);
  }, [visible, query, roleFilter]);

  const countFor = (role: Role) => visible.filter((p) => p.activeCount > 0 && p.role === role).length;

  return (
    <Box>
      {/* ── Filters ── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
          <Chip
            label={`All · ${visible.filter((p) => p.activeCount > 0).length}`}
            onClick={() => setRoleFilter('all')}
            color={roleFilter === 'all' ? 'primary' : 'default'}
            variant={roleFilter === 'all' ? 'filled' : 'outlined'}
          />
          {ROLES.filter((r) => r.role).map((r) => {
            const n = countFor(r.role as Role);
            if (!n) return null;
            const on = roleFilter === r.role;
            return (
              <Chip
                key={r.role}
                label={`${r.title} · ${n}`}
                onClick={() => setRoleFilter(on ? 'all' : (r.role as Role))}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
              />
            );
          })}
        </Stack>
        <TextField
          size="small"
          placeholder="Search name or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: { xs: '100%', md: 260 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
      </Stack>

      <Box sx={{ border: `1px solid ${colors.divider}`, borderRadius: 3, overflow: 'hidden', bgcolor: colors.white }}>
        {/* Column headings — desktop only; on a phone each row is a card. */}
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' }, gridTemplateColumns: GRID.md, gap: 2,
            px: 2.5, py: 1.25, bgcolor: colors.backgroundTertiary, borderBottom: `1px solid ${colors.divider}`,
          }}
        >
          {['Person', 'Role', 'Locations', ''].map((h) => (
            <Typography key={h} variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
              {h}
            </Typography>
          ))}
        </Box>

        {/* One flat list, already in role order — the Role column says the
            rest, so no group headings. */}
        {groups.map((g) => (
          <React.Fragment key={g.title}>
            {g.people.map((p) => {
              const revoked = p.activeCount === 0;
              const brandWide = p.grants.some((x) => x.status === 'active' && x.scopeKind === 'brand');
              return (
                <Box
                  key={p.key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'auto minmax(0, 1fr) auto', md: GRID.md },
                    gridTemplateAreas: { xs: '"person person actions" "role locs locs"', md: '"person role locs actions"' },
                    rowGap: { xs: 1, md: 0 }, columnGap: 2, alignItems: 'center',
                    px: 2.5, py: 1.5, borderBottom: `1px solid ${colors.divider}`,
                    opacity: revoked ? 0.55 : 1,
                    '&:hover': { bgcolor: colors.backgroundTertiary },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ gridArea: 'person', minWidth: 0 }}>
                    <Avatar sx={{
                      width: 36, height: 36, fontSize: 13, fontWeight: 700,
                      bgcolor: styleFor(revoked ? null : p.role).bg,
                      color: styleFor(revoked ? null : p.role).fg,
                      border: `1px solid ${styleFor(revoked ? null : p.role).border}`,
                    }}>
                      {initials(p.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography fontWeight={600} noWrap>{p.name}</Typography>
                        {p.kind === 'studio' && (
                          <Tooltip title="Signs in as the studio record itself, not as a named person">
                            <Chip size="small" variant="outlined" label="studio login" sx={{ height: 20, fontSize: 11 }} />
                          </Tooltip>
                        )}
                        {!p.isActive && <Chip size="small" color="warning" label="login disabled" sx={{ height: 20, fontSize: 11 }} />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {p.email || 'no email on record'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ gridArea: 'role' }}>
                    <RoleBadge role={revoked ? null : p.role} />
                  </Box>

                  <Box sx={{ gridArea: 'locs', minWidth: 0 }}>
                    <Locations person={p} />
                  </Box>

                  {/* Icons only: pink to edit, red to remove, soft tint behind each. */}
                  <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center" sx={{ gridArea: 'actions' }}>
                    {!revoked && !brandWide && p.role && (
                      <Tooltip title="Change locations">
                        <IconButton
                          size="small"
                          onClick={() => onEditLocations(p)}
                          sx={{ color: colors.primaryDark, bgcolor: `${colors.primary}1F`, '&:hover': { bgcolor: `${colors.primary}40` } }}
                        >
                          <EditLocationAlt fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!revoked && (
                      <Tooltip title="Remove all access">
                        <IconButton
                          size="small"
                          onClick={() => onRemove(p)}
                          sx={{ color: colors.error, bgcolor: `${colors.error}14`, '&:hover': { bgcolor: `${colors.error}29` } }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </React.Fragment>
        ))}

        {!groups.length && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              {visible.length ? 'Nobody matches this filter.' : 'Nobody has access to this brand yet.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BrandAccessList;
